// components/Sidebar.js

import React from 'react';
import { Box, Button, Typography, Slider } from '@mui/material';
import { styled } from '@mui/system';

const SidebarContainer = styled(Box)({
  width: '12.5rem', // 200px
  background: '#ffffff',
  padding: '1rem', // 16px
  boxSizing: 'border-box',
  borderRight: '0.0625rem solid #e0e0e0', // 1px
});

function Sidebar({
  onClearCanvas,
  onToggleDrawing,
  isDrawingEnabled,
  onToggleAddAnnotationMode,
  isAddAnnotationModeEnabled,
  onToggleDimensionLine,
  isDimensionLineEnabled,
  onToggleBalloonMode,
  isBalloonModeEnabled,
  onToggleOrientation,
  isLandscape,
  zoom,
  setZoom,
  textZoom,
  setTextZoom,
  positionTextZoom,
  setPositionTextZoom,
  snapThreshold,
  setSnapThreshold,
  snappingMode,
  setSnappingMode,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}) {

  // Функция для переключения режима привязки
  const toggleSnappingMode = () => {
    if (snappingMode === 'none') {
      setSnappingMode('points');
    } else {
      setSnappingMode('none');
    }
  };

  // Получаем текст для кнопки
  const getSnappingModeText = () => {
    return snappingMode === 'points' ? 'Режим привязки: вкл' : 'Режим привязки: выкл';
  };

  return (
    <SidebarContainer>
      <Typography variant="h6" gutterBottom sx={{ color: '#212121', fontWeight: 600, mb: 2 }}>
        Инструменты
      </Typography>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: '#f5f5f5',
          color: '#424242',
          fontWeight: 500,
          border: '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#e0e0e0',
            border: '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={onToggleOrientation}
      >
        Сменить ориентацию ({isLandscape ? 'Гориз.' : 'Верт.'})
      </Button>
      {/* Масштабы перенесены в кнопку "Масштаб" панели инструментов */}
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: snappingMode === 'points' ? '#d32f2f' : '#f5f5f5',
          color: snappingMode === 'points' ? '#ffffff' : '#424242',
          fontWeight: 500,
          border: snappingMode === 'points' ? '0.0625rem solid #d32f2f' : '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: snappingMode === 'points' ? '#c62828' : '#e0e0e0',
            border: snappingMode === 'points' ? '0.0625rem solid #c62828' : '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={toggleSnappingMode}
      >
        {getSnappingModeText()}
      </Button>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: '#757575', mb: 1 }}>
          Порог привязки: {snapThreshold}px
        </Typography>
        <Slider
          value={snapThreshold}
          min={5}
          max={50}
          step={5}
          onChange={(e, value) => setSnapThreshold(value)}
          color="primary"
        />
      </Box>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: isDrawingEnabled ? '#d32f2f' : '#f5f5f5',
          color: isDrawingEnabled ? '#ffffff' : '#424242',
          fontWeight: 500,
          border: isDrawingEnabled ? '0.0625rem solid #d32f2f' : '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: isDrawingEnabled ? '#c62828' : '#e0e0e0',
            border: isDrawingEnabled ? '0.0625rem solid #c62828' : '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={onToggleDrawing}
      >
        {isDrawingEnabled ? 'Отключить рисование' : 'Включить рисование'}
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: isBalloonModeEnabled ? '#d32f2f' : '#f5f5f5',
          color: isBalloonModeEnabled ? '#ffffff' : '#424242',
          fontWeight: 500,
          border: isBalloonModeEnabled ? '0.0625rem solid #d32f2f' : '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: isBalloonModeEnabled ? '#c62828' : '#e0e0e0',
            border: isBalloonModeEnabled ? '0.0625rem solid #c62828' : '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={onToggleBalloonMode}
      >
        Позиция
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: isAddAnnotationModeEnabled ? '#d32f2f' : '#f5f5f5',
          color: isAddAnnotationModeEnabled ? '#ffffff' : '#424242',
          fontWeight: 500,
          border: isAddAnnotationModeEnabled ? '0.0625rem solid #d32f2f' : '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: isAddAnnotationModeEnabled ? '#c62828' : '#e0e0e0',
            border: isAddAnnotationModeEnabled ? '0.0625rem solid #c62828' : '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={onToggleAddAnnotationMode}
      >
        Добавить текст
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: isDimensionLineEnabled ? '#d32f2f' : '#f5f5f5',
          color: isDimensionLineEnabled ? '#ffffff' : '#424242',
          fontWeight: 500,
          border: isDimensionLineEnabled ? '0.0625rem solid #d32f2f' : '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: isDimensionLineEnabled ? '#c62828' : '#e0e0e0',
            border: isDimensionLineEnabled ? '0.0625rem solid #c62828' : '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={onToggleDimensionLine}
      >
        {isDimensionLineEnabled
          ? 'Отключить размеры'
          : 'Включить размеры'}
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: '#f5f5f5',
          color: '#424242',
          fontWeight: 500,
          border: '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#e0e0e0',
            border: '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: '#fafafa',
            color: '#bdbdbd',
            border: '0.0625rem solid #eeeeee',
          }
        }}
        onClick={onUndo}
        disabled={!canUndo}
      >
        Отменить
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          mb: 1,
          backgroundColor: '#f5f5f5',
          color: '#424242',
          fontWeight: 500,
          border: '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#e0e0e0',
            border: '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: '#fafafa',
            color: '#bdbdbd',
            border: '0.0625rem solid #eeeeee',
          }
        }}
        onClick={onRedo}
        disabled={!canRedo}
      >
        Повторить
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          backgroundColor: '#f44336',
          color: '#ffffff',
          fontWeight: 500,
          border: '0.0625rem solid #f44336',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#d32f2f',
            border: '0.0625rem solid #d32f2f',
            boxShadow: 'none',
          }
        }}
        onClick={onClearCanvas}
      >
        Очистить чертеж
      </Button>
    </SidebarContainer>
  );
}

export default Sidebar;
